import React from 'react';
import SEOHead from '../SEOHead';
import { Card, CardContent } from '../ui/card';

const AboutSongsPage = () => {
  const songs = [
    {
      title: "Ghost In Plain Sight",
      description: "The song portrays the internal battle of living with invisible pain, highlighting the facade of happiness masking deep suffering. It speaks to the isolating nature of hidden struggles, the silent war waged within. Yet, amidst the pain, the lyrics offer a glimmer of hope, emphasizing the importance of love, understanding, and resilience in facing life's unseen battles.",
      spotifyUrl: "https://open.spotify.com/track/0rt1nQOw34V0SXRAw5oOuD"
    },
    {
      title: "From The Inside",
      description: "The song delves into a deep internal struggle, marked by confusion, pain, and haunting memories. It speaks to a desperate search for light amidst darkness, a fight for freedom from unseen inner turmoil. Amidst the chaos, a resilient spirit emerges, determined to rise above the ashes, overcome past traumas, and find healing. The lyrics express a profound yearning for connection and guidance, ultimately culminating in a promise of self-discovery, resilience, and a triumphant rebirth.",
      spotifyUrl: "https://open.spotify.com/track/4E83tT4l2rlPLID47YpwVy"
    },
    {
      title: "Venomous Crusade",
      description: "The song narrates a heartbreaking tale of betrayal and loss. It tells the story of a father who escapes a toxic relationship, finds new love, only to be targeted by his ex-partner's vengeful actions. The lyrics capture the anguish of losing his children to manipulation and false accusations, the pain of a family torn apart. Despite the adversity, the song expresses resilience, hope, and the unwavering love of a father determined to reclaim his family's future.",
      spotifyUrl: "https://open.spotify.com/track/4Fujv6FSCx3EzSLyYO3zlz"
    },
    {
      title: "Masked Reality",
      description: "\"Masked Reality\" is a sharp commentary on the art of deception in both music and everyday life. Inspired by the clever wordplay of rap culture, the song explores how we craft personas and manipulate perceptions through language. It draws parallels between the linguistic dexterity of skilled MCs and the verbal illusions we all create in our daily interactions. The lyrics delve into the struggle for authenticity in a world of carefully curated images, highlighting the mental toll of maintaining these facades. Despite its critical tone, the song also celebrates the artistry of wordplay, inviting listeners to reflect on the power of language and the quest for genuine self-expression in our social media-driven society.",
      spotifyUrl: "https://open.spotify.com/track/15TLRRojTlqRKI3aT8MV0b"
    },
    {
      title: "Autumn's Embrace",
      description: "A powerful exploration of nocturnal anxiety and depression, the song delves into the struggle of sleepless nights and racing thoughts. The metaphor of autumn and falling leaves represents the cyclical nature of mental health struggles. While acknowledging the weight of darkness and internal battles, it ultimately carries a message of resilience and survival, emphasizing that making it through each day is itself a victory worth celebrating.",
      spotifyUrl: "https://open.spotify.com/track/6ydc7efV09laNTLfuzVDPb"
    },
    {
      title: "Screen-Lit Youth",
      description: "A critical examination of modern youth culture from an older perspective, this song expresses concern about digital dependence and its perceived negative impacts. Through its narrative, it presents worries about addiction, financial irresponsibility, and the pursuit of social media validation. The lyrics paint a picture of a generation lost in virtual worlds, highlighting concerns about authenticity, responsibility, and real-world disconnection.",
      spotifyUrl: "https://open.spotify.com/track/2H2FKAtMcmfufnWgeYMg02"
    },
    {
      title: "OK Boomer (Screen-Lit Youth Response)",
      description: "A direct lyrical rebuttal to \"Screen-Lit Youth,\" this track flips the critical narrative on its head. Using the same themes but from the youth perspective, it challenges the hypocrisy of older generations' criticisms while defending digital native culture. The song cleverly mirrors its predecessor's structure while using modern slang and ad-libs to emphasize its counter-narrative, pointing out that older generations have their own forms of dependencies and limitations while the digital generation builds new forms of connection and community.",
      spotifyUrl: "https://open.spotify.com/track/6cExbnvow5Vsw8M5Wg3atS"
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    "name": "Exlo Song Meanings and Stories",
    "description": "Discover the deep meanings behind Exlo's songs. Each track explores themes of resilience, hope, and human emotion through digital sound.",
    "url": "https://music.exlo.no/about-songs",
    "author": {
      "@type": "MusicGroup",
      "name": "Exlo",
      "url": "https://music.exlo.no"
    },
    "genre": ["Electronic", "AI-Assisted Music", "Digital Music"],
    "about": songs.map(song => ({
      "@type": "MusicRecording",
      "name": song.title,
      "description": song.description,
      "url": song.spotifyUrl
    }))
  };

  return (
    <div className="min-h-screen bg-[#1a1a2e] text-white">
      <SEOHead
        title="Exlo Song Meanings - Stories Behind the Electronic Music"
        description="Discover the deep meanings behind Exlo's songs. Each track explores themes of resilience, hope, and human emotion through digital sound."
        url="/about-songs"
        structuredData={structuredData}
      />
      
      <noscript>
        <div className="p-8">
          <h1 className="text-3xl font-bold mb-4 text-[#e94560]">About the Songs</h1>
          <p className="text-lg mb-6">Discover the deep meanings behind Exlo's electronic music:</p>
          {songs.map((song, index) => (
            <div key={index} className="mb-6">
              <h2 className="text-xl font-bold mb-2 text-[#e94560]">{song.title}</h2>
              <p className="text-gray-300 mb-2">{song.description}</p>
              <a href={song.spotifyUrl} className="text-cyan-400 underline">Listen on Spotify</a>
            </div>
          ))}
        </div>
      </noscript>

      <div className="p-4 md:p-8 max-w-6xl mx-auto">
        <header className="mb-8">
          <h1 className="text-4xl font-bold mb-4 text-[#e94560]">About the Songs</h1>
          <p className="text-lg text-gray-300">Discover the deep meanings behind Exlo's electronic music. Each track explores themes of resilience, hope, and human emotion through digital sound.</p>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {songs.map((song, index) => (
            <Card key={index} className="bg-transparent border-[#e94560] hover:border-cyan-400 transition-colors">
              <CardContent className="p-4">
                <h2 className="text-xl font-bold mb-2 text-[#e94560]">{song.title}</h2>
                <p className="text-white text-lg font-light leading-relaxed mb-4">{song.description}</p>
                <a 
                  href={song.spotifyUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyan-400 underline hover:text-cyan-300 transition-colors"
                >
                  Listen on Spotify
                </a>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AboutSongsPage;
